import { MonoTypeOperatorFunction, Observable, of, retry, throwError, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export interface RetryConfig {
	maxRetries?: number;
	skipRetryForStatusCodes?: number[];
	baseDelayMs?: number;
}

function extractStatusCode(value: unknown): number | undefined {
	const obj = value as {
		status?: number;
		statusCode?: number;
		result?: {
			status?: number;
			statusCode?: number;
		};
	};
	return obj?.result?.statusCode ?? obj?.result?.status ?? obj?.statusCode ?? obj?.status;
}

function isErrorStatus(status: number | undefined): boolean {
	return status !== undefined && (status < 200 || status >= 300);
}

export function withCustomRetry<T>(config?: RetryConfig): MonoTypeOperatorFunction<T> {
	const maxRetries = config?.maxRetries ?? 3;
	const skipRetryForStatusCodes = config?.skipRetryForStatusCodes ?? [400, 401, 403, 409];
	const baseDelayMs = config?.baseDelayMs ?? 50;

	return (source: Observable<T>) =>
		source.pipe(
			switchMap((response) => {
				const status = extractStatusCode(response);
				if (isErrorStatus(status)) {
					return throwError(() => response);
				}
				return of(response);
			}),
			retry({
				count: maxRetries,
				delay: (error: unknown, attempt: number) => {
					const status = extractStatusCode(error);
					if (status && skipRetryForStatusCodes.includes(status)) {
						return throwError(() => error);
					}
					const delayMs = baseDelayMs * 2 ** (attempt - 1);
					return timer(delayMs);
				},
			}),
		);
}
